import { useEffect, useState, FormEvent } from "react";
import { LifeBuoy, Phone, MessageCircle, Lightbulb, Send, Clock } from "lucide-react";
import { api, mensajeError } from "../api/client";
import { PageHeader } from "../components/ui/PageHeader";
import { Spinner } from "../components/ui/Spinner";
import { Badge } from "../components/ui/Badge";

type Tipo = "SOPORTE" | "LISTA_DESEOS";
type Estado = "PENDIENTE" | "EN_PROCESO" | "RESUELTO";

interface Solicitud {
  id: string;
  tipo: Tipo;
  asunto: string;
  mensaje: string;
  estado: Estado;
  createdAt: string;
}

function colorEstado(estado: Estado): "amarillo" | "azul" | "verde" {
  if (estado === "RESUELTO") return "verde";
  if (estado === "EN_PROCESO") return "azul";
  return "amarillo";
}

function etiquetaEstado(estado: Estado) {
  if (estado === "RESUELTO") return "Resuelto";
  if (estado === "EN_PROCESO") return "En proceso";
  return "Pendiente";
}

function soloDigitos(telefono: string) {
  return telefono.replace(/\D/g, "");
}

function ListaSolicitudes({ items }: { items: Solicitud[] }) {
  if (items.length === 0) {
    return <p className="text-sm text-slate-400">Todavía no has enviado ninguna solicitud de este tipo.</p>;
  }
  return (
    <div className="space-y-2">
      {items.map((s) => (
        <div key={s.id} className="rounded-xl border border-slate-100 px-3.5 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium text-slate-700">{s.asunto}</p>
            <Badge color={colorEstado(s.estado)}>{etiquetaEstado(s.estado)}</Badge>
          </div>
          <p className="mt-1 text-sm text-slate-500">{s.mensaje}</p>
          <p className="mt-1.5 flex items-center gap-1 text-xs text-slate-400">
            <Clock size={11} /> {new Date(s.createdAt).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" })}
          </p>
        </div>
      ))}
    </div>
  );
}

export default function SoporteTecnico() {
  const [telefonoSoporte, setTelefonoSoporte] = useState<string | null>(null);
  const [solicitudes, setSolicitudes] = useState<Solicitud[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formSoporte, setFormSoporte] = useState({ asunto: "", mensaje: "" });
  const [enviandoSoporte, setEnviandoSoporte] = useState(false);
  const [errorSoporte, setErrorSoporte] = useState<string | null>(null);
  const [okSoporte, setOkSoporte] = useState(false);

  const [formDeseo, setFormDeseo] = useState({ asunto: "", mensaje: "" });
  const [enviandoDeseo, setEnviandoDeseo] = useState(false);
  const [errorDeseo, setErrorDeseo] = useState<string | null>(null);
  const [okDeseo, setOkDeseo] = useState(false);

  async function cargarSolicitudes() {
    const res = await api.get("/soporte");
    setSolicitudes(res.data.items);
  }

  useEffect(() => {
    Promise.all([api.get("/soporte/configuracion"), api.get("/soporte")])
      .then(([resConfig, resSolicitudes]) => {
        setTelefonoSoporte(resConfig.data.telefonoSoporte);
        setSolicitudes(resSolicitudes.data.items);
      })
      .catch((err) => setError(mensajeError(err)))
      .finally(() => setCargando(false));
  }, []);

  async function enviarSoporte(e: FormEvent) {
    e.preventDefault();
    setEnviandoSoporte(true);
    setErrorSoporte(null);
    setOkSoporte(false);
    try {
      await api.post("/soporte", { tipo: "SOPORTE", ...formSoporte });
      setFormSoporte({ asunto: "", mensaje: "" });
      setOkSoporte(true);
      await cargarSolicitudes();
    } catch (err) {
      setErrorSoporte(mensajeError(err));
    } finally {
      setEnviandoSoporte(false);
    }
  }

  async function enviarDeseo(e: FormEvent) {
    e.preventDefault();
    setEnviandoDeseo(true);
    setErrorDeseo(null);
    setOkDeseo(false);
    try {
      await api.post("/soporte", { tipo: "LISTA_DESEOS", ...formDeseo });
      setFormDeseo({ asunto: "", mensaje: "" });
      setOkDeseo(true);
      await cargarSolicitudes();
    } catch (err) {
      setErrorDeseo(mensajeError(err));
    } finally {
      setEnviandoDeseo(false);
    }
  }

  const solicitudesSoporte = solicitudes.filter((s) => s.tipo === "SOPORTE");
  const solicitudesDeseos = solicitudes.filter((s) => s.tipo === "LISTA_DESEOS");

  if (cargando) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        titulo="Soporte técnico"
        descripcion="Contáctanos si tienes dudas o problemas, o cuéntanos qué te gustaría que la plataforma pudiera hacer."
        icono={<LifeBuoy size={22} />}
      />

      {error && <p className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Contactar a soporte */}
        <div className="card p-6">
          <h2 className="mb-1 flex items-center gap-2 text-base font-semibold text-slate-800">
            <LifeBuoy size={18} className="text-brand-600" /> Contactar a soporte
          </h2>
          <p className="mb-4 text-sm text-slate-500">Elige la forma que te sea más fácil.</p>

          <div className="mb-5 flex flex-wrap gap-2.5">
            {telefonoSoporte ? (
              <>
                <a
                  href={`tel:${soloDigitos(telefonoSoporte)}`}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                >
                  <Phone size={15} /> Llamar
                </a>
                <a
                  href={`https://wa.me/${soloDigitos(telefonoSoporte)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-600"
                >
                  <MessageCircle size={15} /> WhatsApp
                </a>
              </>
            ) : (
              <p className="text-sm text-slate-400">
                Todavía no hay un teléfono de contacto configurado — usa el formulario de abajo mientras tanto.
              </p>
            )}
          </div>

          <form onSubmit={enviarSoporte} className="space-y-3 border-t border-slate-100 pt-4">
            {errorSoporte && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{errorSoporte}</p>}
            {okSoporte && (
              <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                Tu solicitud se envió correctamente. Nuestro equipo la revisará pronto.
              </p>
            )}
            <div>
              <label className="label mb-1">Asunto</label>
              <input
                className="input"
                required
                value={formSoporte.asunto}
                onChange={(e) => setFormSoporte((f) => ({ ...f, asunto: e.target.value }))}
                placeholder="Ej. No puedo generar una factura"
              />
            </div>
            <div>
              <label className="label mb-1">Mensaje</label>
              <textarea
                className="input"
                rows={3}
                required
                value={formSoporte.mensaje}
                onChange={(e) => setFormSoporte((f) => ({ ...f, mensaje: e.target.value }))}
                placeholder="Cuéntanos con detalle qué pasó..."
              />
            </div>
            <button type="submit" disabled={enviandoSoporte} className="btn-primary flex items-center gap-1.5 text-sm">
              <Send size={15} /> {enviandoSoporte ? "Enviando..." : "Enviar solicitud"}
            </button>
          </form>

          {solicitudesSoporte.length > 0 && (
            <div className="mt-5 border-t border-slate-100 pt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Mis solicitudes</p>
              <ListaSolicitudes items={solicitudesSoporte} />
            </div>
          )}
        </div>

        {/* Lista de deseos */}
        <div className="card p-6">
          <h2 className="mb-1 flex items-center gap-2 text-base font-semibold text-slate-800">
            <Lightbulb size={18} className="text-brand-600" /> Lista de deseos
          </h2>
          <p className="mb-4 text-sm text-slate-500">
            ¿Te gustaría que la plataforma tuviera alguna funcionalidad nueva? Descríbela aquí abajo. Nuestro equipo
            de desarrollo evalúa cada propuesta, y las que resulten viables se van agregando en las próximas
            versiones de Mascotas Vet.
          </p>

          <form onSubmit={enviarDeseo} className="space-y-3">
            {errorDeseo && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{errorDeseo}</p>}
            {okDeseo && (
              <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                ¡Gracias por tu idea! La vamos a revisar con el equipo de desarrollo.
              </p>
            )}
            <div>
              <label className="label mb-1">¿Qué te gustaría que se agregara?</label>
              <input
                className="input"
                required
                value={formDeseo.asunto}
                onChange={(e) => setFormDeseo((f) => ({ ...f, asunto: e.target.value }))}
                placeholder="Ej. Recordatorios por correo además de WhatsApp"
              />
            </div>
            <div>
              <label className="label mb-1">Descríbela con más detalle</label>
              <textarea
                className="input"
                rows={3}
                required
                value={formDeseo.mensaje}
                onChange={(e) => setFormDeseo((f) => ({ ...f, mensaje: e.target.value }))}
                placeholder="Cuéntanos cómo te gustaría que funcionara y por qué te sería útil..."
              />
            </div>
            <button type="submit" disabled={enviandoDeseo} className="btn-primary flex items-center gap-1.5 text-sm">
              <Send size={15} /> {enviandoDeseo ? "Enviando..." : "Enviar propuesta"}
            </button>
          </form>

          {solicitudesDeseos.length > 0 && (
            <div className="mt-5 border-t border-slate-100 pt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Mis propuestas</p>
              <ListaSolicitudes items={solicitudesDeseos} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
